// src/config/sentry.config.ts
import * as Sentry from "@sentry/react";

export interface SentryConfig {
  dsn: string;
  environment: string;
  tracesSampleRate: number;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
  enabled: boolean;
}

/**
 * Get the current environment from Vite mode
 */
const getEnvironment = (): string => {
  const mode = import.meta.env.MODE;

  if (mode === "production") return "production";
  if (mode === "testing") return "testing";
  return "development";
};

/**
 * Get environment-specific Sentry DSN
 */
const getSentryDSN = (): string => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.warn("VITE_SENTRY_DSN is not defined. Sentry will be disabled.");
    return "";
  }

  return dsn;
};

/**
 * Get Sentry configuration based on environment
 */
export const getSentryConfig = (): SentryConfig => {
  const environment = getEnvironment();
  const dsn = getSentryDSN();

  // Base configuration
  const baseConfig: SentryConfig = {
    dsn,
    environment,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enabled: !!dsn,
  };

  // Environment-specific overrides
  if (environment === "production") {
    return {
      ...baseConfig,
      tracesSampleRate: 0.2, // Sample 20% of transactions in production
      replaysSessionSampleRate: 0.05, // Sample 5% of sessions in production
    };
  }

  if (environment === "testing") {
    return {
      ...baseConfig,
      tracesSampleRate: 0.5, // Sample 50% of transactions in testing
      replaysSessionSampleRate: 0.1, // Sample 10% of sessions in testing
    };
  }

  // Development environment
  return {
    ...baseConfig,
    tracesSampleRate: 1.0, // Capture 100% of transactions in development
    replaysSessionSampleRate: 1.0, // Capture 100% of sessions in development
  };
};

/**
 * Initialize Sentry with the appropriate configuration
 */
export const initializeSentry = () => {
  const config = getSentryConfig();

  if (!config.enabled) {
    console.warn("Sentry is disabled because VITE_SENTRY_DSN is not configured.");
    return;
  }

  try {
    Sentry.init({
    dsn: config.dsn,
    environment: config.environment,

    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text content for privacy (forms may contain sensitive data)
        maskAllText: true,
        // Block all media for privacy
        blockAllMedia: true,
        // Mask all inputs
        maskAllInputs: true,
      }),
    ],

    // Set trace propagation targets for distributed tracing
    tracePropagationTargets: [
      "localhost",
      /^\//,
      /^https:\/\/api\.dev\.guardiancheckin\.com/,
      /^https:\/\/api\.guardiancheckin\.com/,
    ],

    // Tracing
    tracesSampleRate: config.tracesSampleRate,

    // Session Replay
    replaysSessionSampleRate: config.replaysSessionSampleRate,
    replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,

    // Don't send PII (important for forms with guest data)
    sendDefaultPii: false,

    // Filter before sending to Sentry
    beforeSend: (event, hint) => {
      // Filter out specific errors if needed
      if (event.exception) {
        const error = hint.originalException as Error;

        // CRITICAL: Don't send Sentry's own errors to prevent infinite loops
        if (error?.message?.includes("sentry") || error?.message?.includes("Sentry")) {
          console.warn("Blocked Sentry error from being sent to Sentry:", error?.message);
          return null;
        }

        // Block rate limit errors (429) from creating cascading failures
        if (error?.message?.includes("429") || error?.message?.includes("rate limit")) {
          console.warn("Blocked rate limit error from being sent to Sentry");
          return null;
        }

        // Don't send network errors that are expected
        if (error?.message?.includes("Network Error")) {
          return null;
        }

        // Don't send cancelled requests
        if (error?.message?.includes("cancelled") || error?.message?.includes("aborted")) {
          return null;
        }

        // Don't send validation errors (these are user errors, not bugs)
        if (error?.message?.includes("validation")) {
          return null;
        }
      }

      // Filter out Sentry API errors by checking the request URL.
      // Match the ingest hostname specifically — `.includes("sentry")` was
      // a substring trap that would silently drop every event whose page
      // URL happened to contain the literal string "sentry" anywhere
      // (e.g. a listing public ID, a query param, a referrer).
      if (event.request?.url?.includes("ingest.sentry.io")) {
        console.warn("Blocked Sentry API error from being reported");
        return null;
      }

      return event;
    },

    // Ignore specific errors
    ignoreErrors: [
      // Sentry's own errors (prevent infinite loops)
      /sentry/i,
      /rate limit/i,
      "429",
      // Sentry performance monitoring (browser extensions / non-standard browsers)
      /featureName.*not found/i,
      "performance metrics was not found",
      // Browser extensions
      "top.GLOBALS",
      "originalCreateNotification",
      "canvas.contentDocument",
      "MyApp_RemoveAllHighlights",
      // Network errors that are expected
      "NetworkError",
      "Network request failed",
      // Aborted requests
      "The user aborted a request",
      "AbortError",
      "CanceledError",
      // Validation errors
      "ValidationError",
      "Validation failed",
      // User cancellations
      "User cancelled",
      "User denied",
    ],

    // Add release information if available
    release: import.meta.env.VITE_APP_VERSION || undefined,

    // Normalize URLs to remove PII
    normalizeDepth: 5,
  });

    console.log(`Sentry initialized for ${config.environment} environment`);
  } catch (error) {
    // If Sentry initialization fails, log the error but don't break the app
    console.error("Failed to initialize Sentry:", error);
    // Continue without Sentry - app functionality is more important than monitoring
  }
};
